import React from 'react';
import { Settings, Code, Search, Plus, Trash2, Edit } from 'lucide-react';
import { Tooltip } from './Tooltip';
import type { Component, ComponentType } from '../schemas/validation';

// Define which component types are invisible
// eslint-disable-next-line react-refresh/only-export-components
export const INVISIBLE_COMPONENT_TYPES: ComponentType[] = ['head-content', 'body-end'];

interface InvisibleComponentsBarProps {
  components: Component[];
  onEdit: (component: Component) => void;
  onDelete: (componentId: string) => void;
  onAdd: () => void;
}

const getComponentIcon = (type: ComponentType) => {
  switch (type) {
    case 'head-content':
      return Search;
    case 'body-end':
      return Code;
    default:
      return Settings;
  }
};

const getComponentLabel = (type: ComponentType) => {
  switch (type) {
    case 'head-content':
      return 'SEO & Meta Tags';
    case 'body-end':
      return 'Scripts & Widgets';
    default:
      return type;
  }
};

const getComponentDescription = (type: ComponentType) => {
  switch (type) {
    case 'head-content':
      return 'Manage page title, meta tags, SEO settings, and analytics';
    case 'body-end':
      return 'Scripts and code to be inserted before closing body tag';
    default:
      return '';
  }
};

export const InvisibleComponentsBar: React.FC<InvisibleComponentsBarProps> = ({
  components,
  onEdit,
  onDelete,
  onAdd,
}) => {
  // Filter invisible components
  const invisibleComponents = components.filter(c => INVISIBLE_COMPONENT_TYPES.includes(c.type));

  // Check which types are already added
  const existingTypes = new Set(invisibleComponents.map(c => c.type));
  const canAddTypes = INVISIBLE_COMPONENT_TYPES.filter(type => !existingTypes.has(type as any));

  if (invisibleComponents.length === 0 && canAddTypes.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Page Settings
            </span>
            <div className="flex items-center space-x-2 ml-4">
              {invisibleComponents.map(component => {
                const Icon = getComponentIcon(component.type);
                const label = getComponentLabel(component.type);
                const description = getComponentDescription(component.type);

                return (
                  <Tooltip key={component.id} content={description}>
                    <div className="relative group">
                      <button
                        onClick={() => onEdit(component)}
                        className={`
                          flex items-center space-x-2 px-3 py-1.5 rounded-lg
                          ${
                            component.visible
                              ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              : 'bg-gray-100 border border-gray-200 text-gray-400'
                          }
                          transition-all duration-200
                        `}
                      >
                        <Icon size={14} />
                        <span className="text-sm font-medium">{label}</span>
                        <Edit
                          size={12}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </button>

                      {/* Delete button - Always visible for invisible components */}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          onDelete(component.id);
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center transition-all hover:bg-red-600 hover:scale-110 shadow-sm"
                        title="Delete this component"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </Tooltip>
                );
              })}

              {/* Add button for missing invisible components */}
              {canAddTypes.length > 0 && (
                <Tooltip content="Add page settings component">
                  <button
                    onClick={onAdd}
                    className="flex items-center space-x-1 px-3 py-1.5 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Plus size={14} />
                    <span className="text-sm">Add Settings</span>
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Status indicators */}
          {invisibleComponents.length > 0 && (
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              {invisibleComponents.some(
                c => c.type === 'head-content' && c.data?.googleAnalyticsId
              ) && (
                <span className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Analytics Active</span>
                </span>
              )}
              {invisibleComponents.some(c => c.type === 'head-content' && c.data?.title) && (
                <span className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>SEO Configured</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
